<template>
  <div>
    <v-row class="mt-2">
      <v-col lg="6">
        <v-row>
          <v-col lg="8">
            <h5>{{ $t('customer_onboarding_ui.basic_readiness') }}</h5>
          </v-col>
          <v-col lg="4">
            <v-progress-linear :value="customer.onboarding.basicPercentComplete" color="success" class="mt-1"/>
          </v-col>
        </v-row>
        <div class="basic-readiness">
          <ul class="onboarding-checklist">
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-success" :checked="!isEmpty(customer.onboarding.basicCriteria.telemetry)"/>
              <span class="checklist-label ml-1">
                {{ $t('customer_onboarding_ui.vehicle_sensor_data') }}
                <vf-tooltip :text="$tc('customer_onboarding_ui.vehicle_sensor_data_help')">
                  <v-icon>mdi-information</v-icon>
                </vf-tooltip>
              </span>
              <div class="sub-criterion">
              <span v-for="(zone, index) in customer.onboarding.basicCriteria.telemetry" :key="index" class="mr-2">
                <checklist-checkmark checkmark-class="text-success" :checked="zone"/>
                <span class="ml-1">{{ $t(index) }}</span>
              </span>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-success" :checked="customer.onboarding.basicCriteria.products > 0"/>
              <span class="checklist-label ml-1">{{ $tc('product', 2) }}</span>
              <div class="sub-criterion">
                {{ customer.onboarding.basicCriteria.products }} {{ $tc('product', 2) }}
                <router-link to="/products" class="ml-2">
                  <v-icon>mdi-cog</v-icon>
                  {{ $t('configure') }}
                </router-link>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-success" :checked="customer.onboarding.basicCriteria.users > 0"/>
              <span class="checklist-label ml-1">{{ $tc('user', 2) }}</span>
              <div class="sub-criterion">
                {{ customer.onboarding.basicCriteria.users }} {{ $tc('user', 2) }}
                <router-link to="/users" class="ml-2">
                  <v-icon>mdi-cog</v-icon>
                  {{ $t('configure') }}
                </router-link>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-success" :checked="!isEmpty(customer.onboarding.basicCriteria.masterRouteList)"/>
              <span class="checklist-label ml-1">
                {{ $tc('master_route_list') }}
                <vf-tooltip :text="$tc('customer_onboarding_ui.master_route_list_help')">
                  <v-icon>mdi-information</v-icon>
                </vf-tooltip>
              </span>
              <div class="sub-criterion">
                {{ customer.onboarding.basicCriteria.masterRouteList }}
                <router-link to="#general">
                  {{ $t('configure') }}
                </router-link>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-success" :checked="!isEmpty(customer.onboarding.basicCriteria.tempFormat)"/>
              <span class="checklist-label ml-1">
                {{ $tc('temperature_format') }}
                <vf-tooltip :text="$tc('customer_onboarding_ui.temperature_format_help')">
                  <v-icon>mdi-information</v-icon>
                </vf-tooltip>
              </span>
              <div class="sub-criterion">
                {{ customer.onboarding.basicCriteria.tempFormat === 'F' ? $t('fahrenheit') : $t('celsius') }}
                <router-link to="#general" class="ml-2">
                  <v-icon>mdi-cog</v-icon>
                  {{ $t('configure') }}
                </router-link>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-success" :checked="!isEmpty(customer.onboarding.basicCriteria.timezone)"/>
              <span class="checklist-label ml-1">
                {{ $tc('timezone') }}
                <vf-tooltip :text="$tc('customer_onboarding_ui.time_zone_help')">
                  <v-icon>mdi-information</v-icon>
                </vf-tooltip>
              </span>
              <div class="sub-criterion">
                {{ customer.onboarding.basicCriteria.timezone }}
                <router-link to="#general" class="ml-2">
                  <v-icon>mdi-cog</v-icon>
                  {{ $t('configure') }}
                </router-link>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-success" :checked="!isEmpty(customer.onboarding.basicCriteria.dataGapStrategy)"/>
              <span class="checklist-label ml-1">
                {{ $tc('customer_onboarding_ui.data_gap_strategy') }}
                <vf-tooltip :text="$tc('customer_onboarding_ui.data_gap_strategy_help')">
                  <v-icon>mdi-information</v-icon>
                </vf-tooltip>
              </span>
              <div class="sub-criterion">
                {{ customer.onboarding.basicCriteria.dataGapStrategy }}
                <router-link to="#features" class="ml-2">
                  <v-icon>mdi-cog</v-icon>
                  {{ $t('configure') }}
                </router-link>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-success" :checked="Boolean(customer.onboarding.basicCriteria.notifications)"/>
              <span class="checklist-label ml-1">
                {{ $tc('notification', 2) }}
                <vf-tooltip :text="$tc('customer_onboarding_ui.notifications_help')">
                  <v-icon>mdi-information</v-icon>
                </vf-tooltip>
              </span>
              <div class="sub-criterion">
                <v-checkbox
                  v-model="notificationsEnabled"
                  :label="$t('ready')" >
                </v-checkbox>
                <router-link to="/users">
                  <v-icon>mdi-cog</v-icon>
                  {{ $t('customer_onboarding_ui.view_users') }}
                </router-link>
              </div>
            </li>
          </ul>
        </div>
      </v-col>
      <v-col lg="6">
        <v-row>
          <v-col lg="8">
            <h5>{{ $t('customer_onboarding_ui.enterprise_readiness') }}</h5>
          </v-col>
          <v-col lg="4">
            <v-progress-linear :value="customer.onboarding.enterprisePercentComplete" class="mt-1"/>
          </v-col>
        </v-row>
        <div class="enterprise-readiness">
          <ul class="onboarding-checklist">
            <li class="mb-3">
              <checklist-checkmark size="2x" checkmark-class="text-primary" :checked="customer.onboarding.enterpriseCriteria.gpsTelemetry"/>
              <span class="checklist-label ml-1">
                {{ $t('customer_onboarding_ui.vehicle_gps_data') }}
                <vf-tooltip :text="$tc('customer_onboarding_ui.vehicle_gps_data_help')">
                  <v-icon>mdi-information</v-icon>
                </vf-tooltip>
              </span>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-primary" :checked="customer.onboarding.enterpriseCriteria.stopLocations > 0"/>
              <span class="checklist-label ml-1">{{ $tc('delivery_location', customer.onboarding.enterpriseCriteria.stopLocations) }}</span>
              <div class="sub-criterion">
                {{ customer.onboarding.enterpriseCriteria.stopLocations }} {{ $tc('location', customer.onboarding.enterpriseCriteria.stopLocations) }}
                <router-link to="/locations" class="ml-2">
                  <v-icon>mdi-cog</v-icon>
                  {{ $t('configure') }}
                </router-link>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-primary" :checked="customer.onboarding.enterpriseCriteria.dcLocations > 0"/>
              <span class="checklist-label ml-1">{{ $tc('dc_location', 2) }}</span>
              <div class="sub-criterion">
                {{ customer.onboarding.enterpriseCriteria.dcLocations }} {{ $tc('location', customer.onboarding.enterpriseCriteria.dcLocations) }}
                <router-link to="/locations" class="ml-2">
                  <v-icon>mdi-cog</v-icon>
                  {{ $t('configure') }}
                </router-link>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-primary" :checked="!isEmpty(customer.onboarding.enterpriseCriteria.routePlanFeed)"/>
              <span class="checklist-label ml-1">{{ $tc('route_plan_feed') }}</span>
              <div class="sub-criterion">
                {{ $t('last_received') }}:
                <if-set :value="routePlanFeedTime">
                  <multi-time-display :time="routePlanFeedTime" class="d-inline-block"/>
                </if-set>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-primary" :checked="customer.onboarding.enterpriseCriteria.assessmentTerms"/>
              <span class="checklist-label ml-1">{{ $tc('root_cause_terms') }}</span>
              <div class="sub-criterion">
                <router-link to="#general">
                  <v-icon>mdi-cog</v-icon>
                  {{ $t('configure') }}
                </router-link>
              </div>
            </li>
            <li class="mb-2">
              <checklist-checkmark size="2x" checkmark-class="text-primary" :checked="Boolean(customer.onboarding.enterpriseCriteria.routePlanTimezone)"/>
              <span class="checklist-label ml-1">
                {{ $tc('customer_onboarding_ui.route_plan_timezone') }}
                <vf-tooltip :text="$tc('customer_onboarding_ui.route_plan_timezone_help')">
                  <v-icon>mdi-information</v-icon>
                </vf-tooltip>
              </span>
              <div class="sub-criterion">
                <v-checkbox
                  class="d-inline-block"
                  v-model="routePlanTimezoneEnabled"
                  :label="$t('ready')"
                  inline>
                </v-checkbox>
              </div>
            </li>
          </ul>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ChecklistCheckmark from '@/components/customer/ChecklistCheckmark'
import VfTooltip from '@/components/shared/VfTooltip'
import { isEmpty, get } from 'lodash'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import Moment from 'moment-timezone'

export default {
  name: 'CustomerOnboarding',
  props: {
    customer: Object
  },
  components: {
    MultiTimeDisplay,
    ChecklistCheckmark,
    VfTooltip
  },
  mixins: [
    ApiUtilitiesMixin
  ],
  data () {
    return {
      isEmpty: isEmpty
    }
  },
  methods: {
    /**
     * Convenience method to handle updating a customer setting (including handling success and errors)
     * @param obj
     */
    updateCustomerSetting (obj) {
      this.customer.updateSettings(obj)
        .then(resp => {
          this.$store.dispatch(
            'globalMessagingModule/showSuccessFlashBanner',
            this.$t('customer_general_settings_ui.customer_settings_saved')
          )
        })
        .catch(error => {
          if (error) {
            this.renderHttpAdapterErrorBanner(error)
            throw (error)
          }
        })
    }
  },
  computed: {
    /**
     * Computed property proxy for form checkbox.
     * Bootstrap Vue doesn't seem to be able to handle integer 1 and 0 for checkbox values.
     * Updates form locally as well as pushing an update to the customer settings API.
     */
    notificationsEnabled: {
      get () {
        return this.customer.onboarding.basicCriteria.notifications === 1
      },
      set (value) {
        const newValue = value ? 1 : 0
        this.customer.onboarding.basicCriteria.notifications = newValue
        this.updateCustomerSetting({
          onboardingNotificationsConfigured: newValue
        })
      }
    },
    /**
     * @see notificationsEnabled
     */
    routePlanTimezoneEnabled: {
      get () {
        return this.customer.onboarding.enterpriseCriteria.routePlanTimezone === 1
      },
      set (value) {
        const newValue = value ? 1 : 0
        this.customer.onboarding.enterpriseCriteria.routePlanTimezone = newValue
        this.updateCustomerSetting({
          onboardingRoutePlanTimezoneConfigured: newValue
        })
      }
    },
    routePlanFeedTime () {
      const feedTime = get(this.customer, 'onboarding.enterpriseCriteria.routePlanFeed')
      return Moment(feedTime).isValid() ? feedTime : null
    }
  }
}
</script>

<style scoped>
  h4 {
    font-size: 1.25rem;
  }

  ul.onboarding-checklist {
    list-style-type: none;
    padding: 0;
    margin: 0;
    font-size: .75rem;
  }
  .checklist-label {
    vertical-align: middle;
    font-size: 1.25em;
    font-weight: bold;
  }

  .sub-criterion {
    margin-left: 2rem;
  }

  .enterprise-readiness {
    border: 2px solid #337ab7;
    border-radius: 15px;
    padding: 1.5rem;
  }

  .basic-readiness {
    border: 2px solid #30a216;
    border-radius: 15px;
    padding: 1.5rem;
  }

</style>
